// src/features/satisfaction/components/SatisfactionPage.tsx
//
// «Satisfacción» — route /satisfaction, Dirección. The survey the guest
// answers at the end of the journey (on the kiosk screen or with the
// assistant, on the phone or on the kiosk): four figures, the split by
// channel and the responses themselves, for one property
// (`PropertySelector`), the shared period and an optional channel.
// A property that never sent a survey and a period without answers are said
// in words; a mean without answers is «Sin respuestas», never 0 nor a dash.
import React, { useState } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import {
  SentimentSatisfiedAltRounded as OverallIcon,
  TouchAppRounded as EaseIcon,
  HowToRegRounded as ResponsesIcon,
  ChatBubbleOutlineRounded as CommentsIcon,
  AltRouteRounded as ChannelIcon,
  MarkChatReadRounded as NoFeedIcon,
  EventBusyRounded as NoResponsesIcon,
} from '@mui/icons-material';
import { useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { KpiHero } from '@/components/common/KpiHero';
import { WidgetCard } from '@/components/common/WidgetCard';
import { EmptyState } from '@/components/common/EmptyState';
import { ResponsiveMetricsTable } from '@/components/common/ResponsiveMetricsTable';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { withCheckinOrSurveyData } from '@/lib/v2Properties';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { formatCount, formatPct } from '@/lib/analyticsFormat';
import { SURVEY_CHANNELS, sortByChannel, surveyChannelLabel, surveyMeanTile, surveyPageState } from '@/lib/surveyView';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { SurveyResponsesCard, SURVEY_PAGE_SIZES } from './SurveyResponsesCard';
import type { SurveyChannel, SurveyResponsesPage, SurveySummaryResponse } from '@/types/analyticsV2';

const ALL_CHANNELS = 'all';

export const SatisfactionPage: React.FC = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('satisfaction', withCheckinOrSurveyData);
  const { propertyId } = property;
  const [period, setPeriod] = useAnalyticsPeriod();

  const [channelFilter, setChannelFilter] = useState<string>(ALL_CHANNELS);
  const channel = channelFilter === ALL_CHANNELS ? undefined : (channelFilter as SurveyChannel);
  const [withComment, setWithComment] = useState(false);
  const [pageSize, setPageSize] = useState(SURVEY_PAGE_SIZES[0]);

  // The page number belongs to one set of filters: any filter change starts
  // again at page 1 without a second request for the stale page.
  const filterKey = [propertyId, period.from, period.to, channelFilter, withComment, pageSize].join('|');
  const [pageState, setPageState] = useState({ key: filterKey, page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;

  const summary = useAnalyticsFetch<SurveySummaryResponse>(
    () => (dataProvider as any).getSurveySummary({ property: propertyId, from: period.from, to: period.to, channel }),
    [dataProvider, propertyId, period.from, period.to, channel],
    Boolean(propertyId)
  );

  const responses = useAnalyticsFetch<SurveyResponsesPage>(
    () =>
      (dataProvider as any).getSurveyResponses({
        property: propertyId,
        from: period.from,
        to: period.to,
        channel,
        withComment,
        page,
        pageSize,
      }),
    [dataProvider, propertyId, period.from, period.to, channel, withComment, page, pageSize],
    Boolean(propertyId)
  );

  const timeZone = property.properties.find((p) => p.id === propertyId)?.timezone;
  const state = summary.data ? surveyPageState(summary.data) : null;
  const t = (key: string, options?: Record<string, unknown>) => translate(`resources.satisfaction.${key}`, options);

  const channelSelect = (
    <TextField
      select
      size="small"
      label={t('filters.channel')}
      value={channelFilter}
      onChange={(e) => setChannelFilter(e.target.value)}
      sx={{ width: { xs: '100%', sm: 260 } }}
    >
      <MenuItem value={ALL_CHANNELS}>{t('filters.allChannels')}</MenuItem>
      {SURVEY_CHANNELS.map((c) => (
        <MenuItem key={c} value={c}>
          {surveyChannelLabel(c, translate)}
        </MenuItem>
      ))}
    </TextField>
  );

  const renderBody = () => {
    if (summary.error) return <FetchErrorBanner onRetry={summary.refresh} />;

    if (!summary.loading && state === 'noFeed') {
      return <EmptyState icon={<NoFeedIcon />} message={t('empty.noFeed')} description={t('empty.noFeedHint')} minHeight={240} />;
    }
    if (!summary.loading && state === 'noResponses') {
      return (
        <EmptyState icon={<NoResponsesIcon />} message={t('empty.noResponses')} description={t('empty.noResponsesHint')} minHeight={240} />
      );
    }

    const data = summary.loading ? null : summary.data;
    const overall = data ? surveyMeanTile(data.overall, translate, locale) : null;
    const ease = data ? surveyMeanTile(data.ease, translate, locale) : null;
    const loading = !data;
    const channelRows = sortByChannel(data?.byChannel ?? []);
    const channelTotal = channelRows.reduce((sum, row) => sum + row.responses, 0);
    const channelShare = (count: number) => (channelTotal > 0 ? (count / channelTotal) * 100 : null);

    return (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2.5 }}>
          <KpiHero
            label={t('tiles.overall')}
            icon={<OverallIcon />}
            loading={loading}
            value={overall?.value ?? ''}
            sublabel={overall?.sublabel}
            note={t('tiles.scale')}
            empty={overall?.empty}
          />
          <KpiHero
            label={t('tiles.ease')}
            icon={<EaseIcon />}
            loading={loading}
            value={ease?.value ?? ''}
            sublabel={ease?.sublabel}
            note={t('tiles.scale')}
            empty={ease?.empty}
          />
          <KpiHero
            label={t('tiles.responses')}
            icon={<ResponsesIcon />}
            loading={loading}
            value={data ? formatCount(data.responses) : ''}
            note={t('tiles.responsesNote')}
          />
          <KpiHero
            label={t('tiles.comments')}
            icon={<CommentsIcon />}
            loading={loading}
            value={data ? formatCount(data.comments) : ''}
            note={t('tiles.commentsNote')}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
          <WidgetCard title={t('byChannel.title')} icon={<ChannelIcon />}>
            <ResponsiveMetricsTable
              columns={[
                { key: 'channel', label: t('byChannel.channel'), render: (row) => surveyChannelLabel(row.channel, translate) },
                { key: 'responses', label: t('byChannel.responses'), align: 'right', render: (row) => formatCount(row.responses) },
                { key: 'share', label: t('byChannel.share'), align: 'right', render: (row) => formatPct(channelShare(row.responses), 0) },
              ]}
              rows={channelRows}
              getRowKey={(row) => row.channel}
              loading={loading}
              skeletonRowCount={3}
            />
          </WidgetCard>

          <SurveyResponsesCard
            data={responses.data}
            loading={responses.loading}
            error={responses.error}
            onRetry={responses.refresh}
            page={page}
            pageSize={pageSize}
            onPageChange={(next) => setPageState({ key: filterKey, page: next })}
            onPageSizeChange={setPageSize}
            withComment={withComment}
            onWithCommentChange={setWithComment}
            timeZone={timeZone}
            locale={locale}
          />
        </Box>
      </>
    );
  };

  return (
    <Box>
      <SectionHeader manualScreen="satisfaction"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
            {propertyId && channelSelect}
          </Box>
        }
        subtitle={t('subtitle')}
        helpConfig={{
          titleKey: 'resources.satisfaction.help.title',
          descriptionKey: 'resources.satisfaction.help.description',
          featureKeys: ['resources.satisfaction.help.features.tiles', 'resources.satisfaction.help.features.list'],
          tipKeys: ['resources.satisfaction.help.tips.property'],
        }}
      />

      {!propertyId ? <PropertyState selection={property} /> : renderBody()}
    </Box>
  );
};

export default SatisfactionPage;
